import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Settings as SettingsIcon, Upload, Save, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useT } from '../contexts/LanguageContext';
import { CURRENCIES, symbolFor } from '../lib/currencies';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO'];

export default function Settings() {
  const { user } = useContext(AuthContext);
  const t = useT();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const r = await axios.get(`${API}/companies`, { withCredentials: true });
      const my = r.data.find(x => x.id === user?.company_id) || r.data[0];
      setCompany(my);
    } catch { toast.error('Failed to load company'); }
    setLoading(false);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB. Use a compressed PNG.');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setCompany(prev => ({ ...prev, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!MANAGE.includes(user?.role)) { toast.error('Not authorized'); return; }
    setSaving(true);
    try {
      // Bug fix Feb-17: only include `plan` if the caller is SUPERADMIN (the
      // only role permitted to change it server-side). Sending it unchanged
      // used to 403 the whole save, blocking the Arabic toggle etc.
      const payload = {
        name: company.name,
        address: company.address,
        contact_email: company.contact_email,
        contact_phone: company.contact_phone,
        logo_url: company.logo_url,
        currency: company.currency,
        currency_symbol: company.currency_symbol,
        arabic_enabled: !!company.arabic_enabled,
        tax_enabled: !!company.tax_enabled,
        tax_label: company.tax_label || 'VAT',
        tax_rate: Number(company.tax_rate) || 0,
        tax_registration_number: company.tax_registration_number || '',
        tax_inclusive_default: !!company.tax_inclusive_default,
      };
      if (user?.role === 'SUPERADMIN') payload.plan = company.plan;
      await axios.put(`${API}/companies/${company.id}`, payload, { withCredentials: true });
      toast.success('Settings saved — logo, currency & language apply across the workspace');
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading) return <Layout><div className="py-12 text-center text-muted-foreground">Loading…</div></Layout>;
  if (!company) return <Layout><div className="py-12 text-center text-muted-foreground">No company assigned</div></Layout>;

  return (
    <Layout>
      <div data-testid="settings-page" className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-2"><SettingsIcon className="w-6 h-6 text-amber-600" />{t('settings.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('settings.subtitle')}</p>
        </div>

        <Card className="p-6">
          <h2 className="font-semibold text-foreground mb-4">{t('settings.branding')}</h2>
          <div className="flex items-start gap-6">
            <div className="w-32 h-32 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center bg-slate-50 overflow-hidden">
              {company.logo_url ? (
                <img src={company.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon className="w-10 h-10 text-slate-300" />
              )}
            </div>
            <div className="flex-1 space-y-3">
              <Label>Company Logo</Label>
              <label data-testid="upload-logo-label" htmlFor="logo-input" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium cursor-pointer">
                <Upload className="w-4 h-4" />Upload Logo
              </label>
              <input data-testid="logo-input" id="logo-input" type="file" accept="image/png,image/jpeg" onChange={handleLogoUpload} className="hidden" />
              {company.logo_url && (
                <Button data-testid="remove-logo-btn" onClick={() => setCompany({ ...company, logo_url: '' })} variant="outline" size="sm">Remove</Button>
              )}
              <p className="text-xs text-muted-foreground">PNG or JPG · max 500KB · square logo recommended</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-foreground mb-4">{t('settings.language')}</h2>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium text-slate-900">{t('settings.enableArabic')}</Label>
              <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                {t('settings.arabicHelp')}
              </p>
              <p className="text-[11px] text-slate-500 mt-2">
                Default is English. This setting is controlled by the App Admin / SUPERADMIN.
              </p>
            </div>
            <button
              type="button"
              data-testid="arabic-toggle"
              onClick={() => MANAGE.includes(user?.role) && setCompany({ ...company, arabic_enabled: !company.arabic_enabled })}
              disabled={!MANAGE.includes(user?.role)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${
                company.arabic_enabled ? 'bg-amber-500' : 'bg-slate-300'
              } ${!MANAGE.includes(user?.role) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              aria-pressed={company.arabic_enabled}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  company.arabic_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {company.arabic_enabled && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900" dir="rtl">
              ✓ تم تمكين الواجهة العربية. سيظهر زر تبديل اللغة في الشريط العلوي لكل مستخدم في شركتك.
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h2 className="font-semibold text-foreground mb-4">Company Details</h2>
          <div className="space-y-4">
            <div><Label>Company Name</Label><Input data-testid="comp-name-input" value={company.name || ''} onChange={(e) => setCompany({ ...company, name: e.target.value })} /></div>
            <div><Label>Address</Label><Textarea rows={2} value={company.address || ''} onChange={(e) => setCompany({ ...company, address: e.target.value })} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Contact Email</Label><Input data-testid="comp-email-input" type="email" value={company.contact_email || ''} onChange={(e) => setCompany({ ...company, contact_email: e.target.value })} /></div>
              <div><Label>Contact Phone</Label><Input value={company.contact_phone || ''} onChange={(e) => setCompany({ ...company, contact_phone: e.target.value })} /></div>
            </div>
            <div><Label>Plan</Label>
              <Select value={company.plan || 'standard'} onValueChange={(v) => setCompany({ ...company, plan: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Currency Code</Label>
                <Select value={company.currency || ''} onValueChange={(v) => {
                  setCompany({ ...company, currency: v, currency_symbol: symbolFor(v) });
                }}>
                  <SelectTrigger data-testid="comp-currency-select"><SelectValue placeholder="Select currency" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {CURRENCIES.map(([code, sym, name]) => (
                      <SelectItem key={code} value={code}>{code} — {name} ({sym})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Display Symbol</Label><Input data-testid="comp-currency-symbol" value={company.currency_symbol || ''} onChange={(e) => setCompany({ ...company, currency_symbol: e.target.value })} placeholder="Auto-filled from code, editable" /></div>
            </div>
          </div>
        </Card>

        {/* Feb-18 — VAT / Tax Settings */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-4">
            VAT / Tax Settings
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            When enabled, the configured rate auto-applies as the default on new LPOs, Quotations and Invoices — editable per document.
          </p>
          <div className="flex items-center justify-between mb-4">
            <div>
              <Label className="font-medium">Enable VAT / Tax</Label>
              <div className="text-xs text-muted-foreground">Turn on to show tax fields across sales & purchase documents.</div>
            </div>
            <button
              type="button"
              onClick={() => MANAGE.includes(user?.role) && setCompany({ ...company, tax_enabled: !company.tax_enabled })}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${company.tax_enabled ? 'bg-amber-500' : 'bg-slate-300'}`}
              data-testid="tax-enable-toggle"
              aria-pressed={company.tax_enabled}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${company.tax_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {company.tax_enabled && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Tax Label</Label>
                <Input data-testid="tax-label-input" value={company.tax_label || 'VAT'} onChange={(e) => setCompany({ ...company, tax_label: e.target.value })} placeholder="VAT" />
                <p className="text-xs text-muted-foreground mt-1">Displayed on documents (e.g. VAT, GST, Sales Tax).</p>
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <Input data-testid="tax-rate-input" type="number" min="0" step="0.01" value={company.tax_rate ?? 0} onChange={(e) => setCompany({ ...company, tax_rate: parseFloat(e.target.value) || 0 })} placeholder="5" />
                <p className="text-xs text-muted-foreground mt-1">UAE standard rate = 5%. Set 0 to disable.</p>
              </div>
              <div>
                <Label>Tax Registration No. (TRN)</Label>
                <Input data-testid="tax-trn-input" value={company.tax_registration_number || ''} onChange={(e) => setCompany({ ...company, tax_registration_number: e.target.value })} placeholder="100xxxxxxxxxx03" />
                <p className="text-xs text-muted-foreground mt-1">Printed on your tax invoices.</p>
              </div>
              <div className="flex items-center justify-between pt-6">
                <div>
                  <Label className="font-medium">Prices are Tax-inclusive by default</Label>
                  <div className="text-xs text-muted-foreground">If on, entered unit prices already include tax.</div>
                </div>
                <button
                  type="button"
                  onClick={() => MANAGE.includes(user?.role) && setCompany({ ...company, tax_inclusive_default: !company.tax_inclusive_default })}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition ${company.tax_inclusive_default ? 'bg-amber-500' : 'bg-slate-300'}`}
                  data-testid="tax-inclusive-toggle"
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${company.tax_inclusive_default ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>
          )}
        </Card>

        {MANAGE.includes(user?.role) && (
          <div className="flex justify-end">
            <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600">
              <Save className="w-4 h-4 mr-1" />{saving ? 'Saving…' : 'Save Settings'}
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
